import {Table, Button, Space, Modal, Tooltip} from "antd";
import {ColumnProps, TablePaginationConfig} from "antd/lib/table";
import {FilterValue, RowSelectionType, SorterResult, TableCurrentDataSource} from "antd/lib/table/interface";
import React, {FC, memo, useEffect, useRef, useState} from "react";
import {omit} from "lodash";
import {Link} from "react-router-dom";
import {blue} from "@ant-design/colors";
import {ArrayDataOptions, BlankMailTypes, Booker, MailDataType, Maybe, ScheduledPaymentType, SortOrder, TablesEnum, useGetCompanyInfoQuery} from "../../generated/graphql";
import {getPagination} from "../../helpers/getPagination";
import {updateFilters} from "../../helpers/updateFilters";
import i18n from "../../services/i18n";
import Section from "../../views/Section/Section";
import {getSortIconTooltipProps, useColumnHeaderDnd} from "../../views/SortIconOnlyHeaderCell/useColumnHeaderDnd";
import ActiveFilters from "../common/tableTools/ActiveFilters";
import TableToolBar, {ReceiverType, useRowSelection} from "../common/tableTools/TableToolBar";
import {MOLLIE_COLUMNS, TableType, useHandleColumns} from "./PaymentsListColumns";
import EditScheduledPayment from "./EditScheduledPayment";
import {marginXs} from "../../styles/layout";
import css from "./Payments.less";
import PaymentsFooter from "./PaymentsFooter";
import useHighestRole, {useIsBetaTester} from "../../helpers/useHighestRole";
import roles from "../../../shared/models/roles";

const checkbox: RowSelectionType = "checkbox";

// Datenfelder ohne eigene Tabellenspalte
type NonColumnKeys = "providerRefundedAmount" | "refundedAmount" | "surplusMolliePaymentCount" | "mollieLinkExpiresAt" | "mollieDispatchAction";

export const allColumns = (): {[key in keyof Omit<TableType, NonColumnKeys>]: string} => {
    const columnNames: {[key in keyof Omit<TableType, NonColumnKeys>]: string} = {
        id: i18n.views.TableColumnTitles.id(),
        paymentDue: i18n.views.TableColumnTitles.paymentDue(),
        paymentAmount: i18n.views.TableColumnTitles.paymentAmount(),
        openAmount: i18n.views.TableColumnTitles.openAmount(),
        createDate: i18n.views.TableColumnTitles.createDate(),
        bookingNumber: i18n.views.TableColumnTitles.bookingNumber(),
        courseType: i18n.views.TableColumnTitles.courseType(),
        courseNumber: i18n.views.TableColumnTitles.courseNumber(),
        booker: i18n.views.TableColumnTitles.booker(),
        attendee: i18n.views.TableColumnTitles.attendee(),
        city: i18n.views.TableColumnTitles.city(),
        location: i18n.views.TableColumnTitles.location(),
        firstCourseLesson: i18n.views.TableColumnTitles.firstCourseLesson(),
        paymentType: i18n.views.TableColumnTitles.paymentType(),
        paymentMethod: i18n.views.TableColumnTitles.paymentMethod(),
        setPaidAction: i18n.views.TableColumnTitles.setPaidAction(),
        setUnPaidAction: i18n.views.TableColumnTitles.setUnPaidAction(),
        invoice: i18n.views.TableColumnTitles.invoice(),
        paymentDone: i18n.views.TableColumnTitles.paymentDone(),
        sepaClearance: i18n.views.TableColumnTitles.sepaClearance(),
        vat: i18n.views.TableColumnTitles.vat(),
        booking: i18n.views.TableColumnTitles.booking(),
        // title: i18n.views.TableColumnTitles.invoiceTitle(),
        invoiceDate: i18n.views.TableColumnTitles.invoiceDate(),
        noticeStaff: i18n.views.TableColumnTitles.noticeStaff(),
        invoiceTitle: i18n.views.TableColumnTitles.invoiceTitle(),
        scheduledPaymentNote: i18n.views.TableColumnTitles.scheduledPaymentNote(),
        nonDisclosureNotice: i18n.views.TableColumnTitles.nonDisclosureNotice(),
        paymentSource: i18n.views.TableColumnTitles.paymentSource(),
        paymentProvider: i18n.views.TableColumnTitles.paymentProvider(),
        mollieStatus: i18n.views.TableColumnTitles.mollieStatus(),
        mollieErrorMessage: i18n.views.TableColumnTitles.mollieErrorMessage(),
        mollieRetryCount: i18n.views.TableColumnTitles.mollieRetryCount(),
        refundStatus: i18n.views.TableColumnTitles.refundStatus(),
        // Phase 16 / AUDIT-02 — Row-Action-Spalte für Timeline-Drawer (HistoryOutlined-Icon).
        paymentActionTimeline: i18n.views.TableColumnTitles.paymentActionTimeline(),
        // B-223 Followup — Status-Spalte mit Tag "Aktiv"/"Gelöscht" + Restore-Action.
        tombstoneStatus: i18n.views.TableColumnTitles.tombstoneStatus(),
        dunningLevel: i18n.views.TableColumnTitles.dunningLevel(),
        type: i18n.views.TableColumnTitles.type(),
    };

    return columnNames;
};

const hiddenColumns: Array<Extract<keyof TableType, string>> = [
    "id",
    "setPaidAction",
    "setUnPaidAction",
    // "title",
];

const standardColumns: Array<Extract<keyof TableType, string>> = [
    "bookingNumber",
    "courseType",
    "courseNumber",
    "booker",
    "attendee",
    "location",
    "firstCourseLesson",
    "invoice",
    "paymentMethod",
    "paymentSource",
    "paymentDue",
    "paymentAmount",
    "setPaidAction",
];

type FooterDataType = Pick<TableType, "paymentAmount">;

type PaymentsListProps = {
    toolBarButtons?: Array<"columnSelector" | "pdf" | "eMail" | "bulkDelete" | "checkList" | "sms">;
    hideCreate?: boolean;
    tableTitle?: React.ReactNode;
    defaultPageSize?: number;
    hideHeader?: boolean;
    defaultColumns?: Array<Extract<keyof TableType, string>>;
    tableData: {
        dataSource: Array<TableType>;
        existMore?: boolean;
        total?: number;
        loading?: boolean;
    };
    createLink?: string;
    editLink?: string;
    detailsLink?: string;
    specialFunctions?: Array<React.ReactNode>;
    selection?: Function;
    minPadding?: boolean;
    hidePagination?: boolean;
    tablesEnum: TablesEnum;
    createXmlBtn?: React.ReactNode;
    options?: Maybe<ArrayDataOptions>;
    setOptions: (options: ArrayDataOptions) => void;
    refetch: () => void;
    style?: React.CSSProperties;
    hiddenFilters?: Array<Extract<keyof TableType, string>>;
    contentType: "openPayment" | "donePayment";
    hideFooter?: boolean;
    mandatoryColumns?: Array<Extract<keyof TableType, string>>;
    extraFunctions?: Array<React.ReactNode>;
    notSelectable?: boolean;
    /** Zusätzliche Zeilenklasse, z. B. Markierung der Live-Aktualisierung. */
    rowClassName?: (record: TableType) => string;
    /** Kurzer Hinweis neben dem Titel (Live-Aktualisierung); der Titel bleibt für den Export-Dateinamen ein String. */
    titleHint?: React.ReactNode;
};

const PaymentsList: FC<PaymentsListProps> = ({
    toolBarButtons = ["columnSelector", "pdf", "eMail", "bulkDelete", "sms"],
    hideCreate = true,
    tableTitle = i18n.containers.payments.PaymentsList.tableTitle(),
    defaultPageSize = 15,
    hideHeader = false,
    defaultColumns = standardColumns,
    tableData,
    createLink = "/",
    editLink = "/",
    detailsLink = "/",
    specialFunctions,
    selection,
    minPadding = false,
    hidePagination,
    tablesEnum,
    createXmlBtn,
    options,
    setOptions,
    refetch,
    style,
    hiddenFilters,
    contentType,
    hideFooter = false,
    mandatoryColumns = ["paymentDue"],
    extraFunctions,
    notSelectable,
    rowClassName,
    titleHint,
}) => {
    const [editPaymentOpen, setEditPaymentOpen] = useState<boolean>(false);
    const [recordToEdit, setRecordToEdit] = useState<TableType | undefined>();
    const [columnsToShow, setColumnsToShow] = useState<Array<Extract<keyof TableType, string>>>([]);
    // Spalten nur mit freigeschaltetem Feature (sc-3370): Mahnstufe nur in den offenen Posten mit featureDunning,
    // Mollie-Spalten nur mit featureMollie. Gilt für den Spaltenwähler UND die gespeicherte Auswahl des Nutzers,
    // die die Toolbar aus der DB zurückspielt (Codex #1547). Die Abfrage ist über die Liste bereits im Cache.
    const {data: companyInfoData} = useGetCompanyInfoQuery({fetchPolicy: "cache-first"});
    const featureMollie = !!companyInfoData?.companyInfo?.subscription?.featureMollie;
    const featureDunning = !!companyInfoData?.companyInfo?.subscription?.featureDunning;
    const hiddenChoices: Array<Extract<keyof TableType, string>> = [
        ...(contentType === "openPayment" && featureDunning ? [] : ["dunningLevel" as const]),
        ...(featureMollie ? [] : MOLLIE_COLUMNS),
    ];
    const columnChoices = omit(allColumns(), hiddenChoices);
    const shownColumns = columnsToShow.filter((col) => hiddenChoices.indexOf(col) === -1);
    const highestRole = useHighestRole();
    const isBetaTester = useIsBetaTester();
    const footerIsToBeShown = [roles.Superuser, roles.SuperAdmin].includes(highestRole) || isBetaTester;

    const pageSizeOptions = ["10", "15", "20", "25"];

    const columns: Array<ColumnProps<TableType>> = useHandleColumns(
        shownColumns,
        options,
        setOptions,
        editLink,
        detailsLink,
        // WP0b: Zeilenliste für die Retry-Bündelung durchreichen.
        tableData.dataSource,
        false,
        options?.filter,
        refetch,
        editPaymentOpen,
        setEditPaymentOpen,
        setRecordToEdit,
    );

    const [selectionType, setSelectionType] = useState<RowSelectionType>(checkbox);
    // B-223 Followup — Tombstones (deletedAt != null) duerfen nicht via Bulk-Selection
    // bezahlt/un-bezahlt markiert werden. Wir geben ihre IDs als `disabled`-Liste an
    // useRowSelection, das die Checkboxen pro-Row disablet.
    const tombstoneIds = (tableData.dataSource ?? [])
        .filter((row) => !!(row as TableType & {deletedAt?: Date | string | null}).deletedAt)
        .map((row) => row.id);
    const rowSelection = useRowSelection({type: selectionType, disabled: tombstoneIds});

    useEffect(() => {
        if (selection) {
            selection(rowSelection.selectedRowKeys);
        }
    }, [rowSelection.selectedRowKeys, selection]);

    const onChange: (
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue | null>,
        sorter: SorterResult<TableType> | Array<SorterResult<TableType>>,
        extra?: TableCurrentDataSource<TableType>,
    ) => void = (pagination, filters, sorter, extra) => {
        const updatedFilters = updateFilters({
            filters,
            options,
            table: "scheduledPayment",
        });

        const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
        const sortColumn = singleSorter?.field as string | undefined;
        const sortOrder = singleSorter?.order === "ascend" ? SortOrder.Ascending : singleSorter?.order === "descend" ? SortOrder.Descending : undefined;

        const updatedOptions: ArrayDataOptions = {
            ...options,
            filter: updatedFilters,
            limit: getPagination({
                pagination,
                defaultSize: defaultPageSize,
            }).limit,
            offset: getPagination({
                pagination,
                defaultSize: defaultPageSize,
            }).offset,
            sortColumn,
            sortOrder,
        };

        setOptions(updatedOptions);
    };

    const renderMenu = () => {
        const menu: Array<React.ReactNode> = [];

        if (extraFunctions) {
            menu.push(extraFunctions);
        }

        if (specialFunctions && rowSelection.selectedRowKeys.length > 0) {
            specialFunctions.forEach((specialFunction) => menu.push(specialFunction));
        }

        if (createXmlBtn) {
            menu.push(createXmlBtn);
        }

        if (!hideCreate) {
            menu.push(
                <Button key="createButton" type="primary" size="small">
                    <Link
                        to={{
                            pathname: createLink,
                        }}
                    >
                        {i18n.containers.payments.PaymentsList.create()}
                    </Link>
                </Button>,
            );
        }

        return <Space>{menu}</Space>;
    };

    const getSelection = (bookingIds: Array<string>): Array<Partial<Booker>> => {
        const rows: Array<TableType> = tableData.dataSource.filter((booking) => bookingIds.includes(booking.id));
        const selection: Array<Partial<Booker>> = [];

        rows.forEach((row) => {
            const booking = row.booking;

            if (booking.attendee?.booker) {
                const booker = booking.attendee.booker;

                selection.push(booker);
            }
        });

        return selection;
    };

    const getReceivers = (bookingIds: Array<string>): Array<ReceiverType> | undefined => {
        const rows: Array<TableType> = tableData.dataSource.filter((booking) => bookingIds.includes(booking.id));
        const receivers: Array<ReceiverType> = [];

        rows.forEach((row) => {
            // console.log("row:::", row);
            if (row.booking.attendee?.booker?.email) {
                const booker = row.booking.attendee.booker;

                const receiver = {
                    key: booker.id,
                    value: booker.email ?? "",
                    label: `${booker.firstname} ${booker.lastname}`,
                };

                receivers.push(receiver);
            }
        });

        return receivers;
    };

    const EditScheduledPaymentModal = () => {
        const modalTitleText = (
            <div
                style={{
                    fontSize: "1.2rem",
                    fontWeight: 600,
                    paddingBottom: 7.5,
                    paddingTop: 7.5,
                    borderBottom: `1px solid ${blue[2]}`,
                    marginBottom: 10,
                }}
            >
                {recordToEdit?.paymentDone === null ? "Offenen Posten bearbeiten" : "Umsatz bearbeiten"}
            </div>
        );

        const modalCloseAfterSubmit = () => {
            setEditPaymentOpen(false);
            setTimeout(() => {
                refetch();
            }, 300);
        };

        return (
            <Modal
                open={editPaymentOpen}
                onCancel={() => setEditPaymentOpen(false)}
                closable
                destroyOnClose={true}
                width={800}
                bodyStyle={{
                    paddingBottom: 7.5,
                    paddingTop: marginXs,
                }}
                footer={null}
            >
                <>
                    {modalTitleText}
                    <EditScheduledPayment record={recordToEdit} setEditPaymentOpen={modalCloseAfterSubmit} refetch={refetch} />
                </>
            </Modal>
        );
    };

    const SpecialPaymentCell: FC<{
        record: TableType;
    }> = ({record, ...restProps}) => {
        const ref = useRef<HTMLTableRowElement>(null);
        const recordTitle = record?.title ?? undefined;

        const className = (record?: TableType) => {
            const type = record?.type;

            const baseClass = (() => {
                switch (type) {
                    case ScheduledPaymentType.Standard:
                        return css.standardPaymentRow;
                    case ScheduledPaymentType.DunningLevel1:
                        return css.specaialPaymentRowDunningLevel1;
                    case ScheduledPaymentType.DunningLevel2:
                        return css.specaialPaymentRowDunningLevel2;
                    case ScheduledPaymentType.DunningLevel3:
                        return css.specaialPaymentRowDunningLevel3;
                    case ScheduledPaymentType.CancelationFee:
                        return css.specaialPaymentRowCancelationFee;
                    case ScheduledPaymentType.Chargeback:
                        return css.specaialPaymentRowChargeback;
                    case ScheduledPaymentType.Custom:
                        return css.specaialPaymentRowCustom;
                    default:
                        return css.standardPaymentRow;
                }
            })();

            // B-223 Followup — Tombstones erhalten zusaetzlich die tombstoneRow-Klasse
            // (Opacity), damit sie visuell als "gelöscht" erkennbar bleiben, wenn der
            // "Gelöschte anzeigen"-Toggle aktiv ist.
            const isTombstone = !!(record as TableType & {deletedAt?: Date | string | null})?.deletedAt;
            const rowClass = isTombstone ? `${baseClass} ${css.tombstoneRow}` : baseClass;

            return record && rowClassName ? `${rowClass} ${rowClassName(record)}` : rowClass;
        };

        return (
            <Tooltip title={recordTitle}>
                <tr ref={ref} {...restProps} className={className(record)}>
                    {}
                </tr>
            </Tooltip>
        );
    };

    const bodyComponents = {
        body: {
            row: SpecialPaymentCell,
        },
    };

    // Generischer Spalten-Header-Drag-and-Drop-Kern. Der Hook ersetzt header.cell
    // durch DraggableSortHeaderCell (erhält dabei das bestehende body.row aus
    // `bodyComponents`), injiziert `onHeaderCell` in die Spalten (`dndColumns`)
    // und liefert `wrapWithDnd` für einen Table-lokalen DndProvider.
    const {components, dndColumns, wrapWithDnd} = useColumnHeaderDnd<TableType>({
        columns,
        columnsToShow: shownColumns,
        setColumnsToShow: (next: Array<string>) => setColumnsToShow(next as Array<Extract<keyof TableType, string>>),
        tablesEnum,
        mandatoryColumns,
        existingComponents: bodyComponents,
    });


    const renderContent = () => {
        return (
            <>
                <EditScheduledPaymentModal />
                <Section
                    title={
                        titleHint ? (
                            <Space>
                                {tableTitle}
                                {titleHint}
                            </Space>
                        ) : (
                            tableTitle
                        )
                    }
                    menu={renderMenu()}
                    minPadding={minPadding}
                    showHeader={!hideHeader}
                    style={style}
                    secondLineLeft={
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignContent: "center",
                                padding: "2px 0px",
                            }}
                        >
                            <TableToolBar
                                buttons={toolBarButtons}
                                columns={columns}
                                allColumns={columnChoices}
                                variableKeys={columnChoices}
                                columnsToShow={shownColumns}
                                setSelectedColumns={(e: string[]) => setColumnsToShow(e as Array<Extract<keyof TableType, string>>)}
                                mandatoryColumns={mandatoryColumns}
                                hiddenColumns={hiddenColumns}
                                rowSelection={rowSelection}
                                selected={getSelection(rowSelection.selectedRowKeys)}
                                selectMode={(type: RowSelectionType) => setSelectionType(type)}
                                exportFileName={typeof tableTitle === "string" ? tableTitle : undefined}
                                dataSource={tableData.dataSource}
                                data={getReceivers(rowSelection.selectedRowKeys)}
                                type={BlankMailTypes.Booking}
                                mailDataType={MailDataType.ScheduledPayment}
                                tablesEnum={tablesEnum}
                                defaultColumns={defaultColumns as Array<string>}
                                activeFilters={<ActiveFilters options={options} setOptions={setOptions} refetch={refetch} style={{flex: 1}} />}
                            />
                        </div>
                    }
                    content={wrapWithDnd(
                        <Table<TableType>
                            size="small"
                            showSorterTooltip={getSortIconTooltipProps()}
                            bordered
                            columns={dndColumns}
                            dataSource={tableData.dataSource}
                            // rowKey="id"
                            rowKey={(record) => record.id}
                            loading={tableData.loading}
                            // rowSelection={rowSelection.result}
                            rowSelection={notSelectable ? undefined : rowSelection.result}
                            sticky={tableData.dataSource.length > 0}
                            scroll={
                                tableData.dataSource.length
                                    ? {
                                          x: `max-content`,
                                          y: "calc(100vh - 280px)",
                                      }
                                    : {x: "auto"}
                            }
                            onChange={onChange}
                            pagination={
                                hidePagination
                                    ? false
                                    : {
                                          showSizeChanger: true,
                                          pageSizeOptions,
                                          defaultPageSize,
                                          total: tableData.total,
                                          hideOnSinglePage: true,
                                      }
                            }
                            footer={() => {
                                if (footerIsToBeShown && !tableData.loading && !hideFooter) {
                                    return <PaymentsFooter contentType={contentType} options={options} />;
                                }

                                return undefined;
                            }}
                            components={components}
                            onRow={(record, rowIndex) => {
                                return {
                                    key: record.id,
                                    index: rowIndex,
                                    record,
                                    onDoubleClick: (event) => {
                                        // B-223 Followup — Tombstones nicht editierbar; Doppelklick
                                        // wuerde sonst den Edit-Drawer mit gelöschten Werten oeffnen.
                                        if ((record as TableType & {deletedAt?: Date | string | null}).deletedAt) {
                                            return;
                                        }
                                        setRecordToEdit(record);
                                        setEditPaymentOpen(true);
                                    },
                                };
                            }}
                        />,
                    )}
                />
            </>
        );
    };

    return renderContent();
};

export default memo(PaymentsList);
